import { IPageRequest } from 'common/interfaces/IPageRequest';
import { ICustomerCardType } from './customer-card';

export type PickupType = 'STATION' | 'DPD';

export enum CustomerApplicationType {
  CREDIT_APPLICATION = 'CREDIT_APPLICATION',
  PAYMENT_CARD_APPLICATION = 'PAYMENT_CARD_APPLICATION',
  DISCOUNT_CARD_APPLICATION = 'DISCOUNT_CARD_APPLICATION',
  INCREASE_CREDIT_APPLICATION = 'INCREASE_CREDIT_APPLICATION',
  /** Only offered in CRM, but the server reports it among the blocking application types. */
  BILLING_TERMS_CHANGE_APPLICATION = 'BILLING_TERMS_CHANGE_APPLICATION'
}

/**
 * The active application types that block a new application of the keyed type. Mirrors the server's
 * CustomerApplicationType.blockedBy(): the two card applications rule each other out, a general credit
 * application rules out everything but a discount card, and a credit increase or billing terms change
 * blocks nothing but a second one of itself.
 */
export const APPLICATION_TYPE_BLOCKED_BY: { [key in CustomerApplicationType]: CustomerApplicationType[] } = {
  [CustomerApplicationType.CREDIT_APPLICATION]: [
    CustomerApplicationType.CREDIT_APPLICATION
  ],
  [CustomerApplicationType.PAYMENT_CARD_APPLICATION]: [
    CustomerApplicationType.CREDIT_APPLICATION,
    CustomerApplicationType.PAYMENT_CARD_APPLICATION,
    CustomerApplicationType.DISCOUNT_CARD_APPLICATION
  ],
  [CustomerApplicationType.DISCOUNT_CARD_APPLICATION]: [
    CustomerApplicationType.PAYMENT_CARD_APPLICATION,
    CustomerApplicationType.DISCOUNT_CARD_APPLICATION
  ],
  [CustomerApplicationType.INCREASE_CREDIT_APPLICATION]: [
    CustomerApplicationType.CREDIT_APPLICATION,
    CustomerApplicationType.INCREASE_CREDIT_APPLICATION
  ],
  [CustomerApplicationType.BILLING_TERMS_CHANGE_APPLICATION]: [
    CustomerApplicationType.CREDIT_APPLICATION,
    CustomerApplicationType.BILLING_TERMS_CHANGE_APPLICATION
  ]
};

/**
 * The notice shown while applications block new ones, naming every distinct blocker. A general credit
 * application is the one type that cannot come round again — approving it creates the credit agreement, which
 * retires the entry point — so a notice about it alone states the fact without promising a new application.
 */
export function blockingApplicationNotice(blocking: BlockingApplication[]): string {
  const distinct = blocking.filter(
    (b, index) => blocking.findIndex(other => other.id === b.id) === index
  );
  if (distinct.length === 0) return '';

  const labels = distinct.map(b => `${CUSTOMER_APPLICATION_TYPE_MAP[b.applicationType].toLowerCase()} (#${b.id})`);
  const listed = labels.length > 1
    ? `${labels.slice(0, -1).join(', ')} ja ${labels[labels.length - 1]}`
    : labels[0];

  if (labels.length > 1) {
    return `Ettevõttel on aktiivsed ${listed} — uue saab esitada pärast nende lõpetamist.`;
  }

  if (distinct[0].applicationType === CustomerApplicationType.CREDIT_APPLICATION) {
    return `Ettevõttel on aktiivne ${listed}.`;
  }

  return `Ettevõttel on aktiivne ${listed} — uue saab esitada pärast selle lõpetamist.`;
}

/** The active application blocking a new one of some type, as reported by the server. */
export interface BlockingApplication {
  id: number;
  applicationType: CustomerApplicationType;
}

/**
 * The blocking map as it stands once an application of the given type has just been created, so the entry
 * points update without waiting for the customer to be refetched.
 */
export function withCreatedApplication(
  blockingApplications: { [key in CustomerApplicationType]?: BlockingApplication },
  createdType: CustomerApplicationType,
  createdId: number
): { [key in CustomerApplicationType]?: BlockingApplication } {
  const updated = { ...blockingApplications };

  Object.values(CustomerApplicationType).forEach(type => {
    if (APPLICATION_TYPE_BLOCKED_BY[type].includes(createdType) && updated[type] == null) {
      updated[type] = { id: createdId, applicationType: createdType };
    }
  });

  return updated;
}

export enum CustomerApplicationStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  WAITING_APPROVAL = 'WAITING_APPROVAL',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  CANCELED = 'CANCELED'
}

export enum CardCategory {
    ALL_FUELS_AND_GOODS = 'ALL_FUELS_AND_GOODS',
    ALL_FUELS_AND_CAR_GOODS = 'ALL_FUELS_AND_CAR_GOODS',
    ALL_FUELS = 'ALL_FUELS',
    DIESEL = 'DIESEL',
    DISCOUNT = 'DISCOUNT'
}

export interface CustomerApplicationCardRequest {
  cardName: string;
  cardLimit?: number;
  cardType: ICustomerCardType;
  cardCategory?: CardCategory;
  customerLocationId?: number;
}

export interface PagedCustomerApplicationsRequest extends IPageRequest {
  type?: CustomerApplicationType;
}

export interface CustomerApplication {
    id: number;
    customer: ApplicationCustomer,
    applicationType: CustomerApplicationType;
    status: CustomerApplicationStatus;
    contact?: ApplicantContactPerson;
    representative?: Representative;
    signers?: ApplicationSigner[];
    invoiceEmail: string;
    isEInvoice: boolean;
    bankAccountNumber?: string;
    purchaseVolume?: string;
    creditLimit?: string;
    paymentDay?: string;
    invoicesPerMonth?: number;
    customerApplicationCards?: CustomerApplicationCards[];
    pickupType: string;
    pickupLocation: string;
    pickupLocationId: string | number;
    recipientEmail?: string;
    recipientPhone?: string;
    comment?: string;
    createdDate: string;
    salesman?: Salesman;
    userOccupation?: string;
}

export enum CardType {
    PAYMENT_CARD = 'PAYMENT_CARD',
    DISCOUNT_CARD = 'DISCOUNT_CARD'
}

interface CustomerApplicationCard {
    cardType: CardType;
    cardCategory: CardCategory;
    cardName: string;
    cardLimit?: string;
    isVirtual?: boolean;
    customerLocationId?: number;
}

interface CustomerApplicationCards {
    cardCategory: CardCategory;
    cardsCount: number;
    cards: CustomerApplicationCard[];
}

interface Salesman {
  id: number;
  personalIdentificationCode: string;
  name: string;
}

interface ApplicantContactPerson {
    id: number;
    name: string;
    email?: string;
    phone?: string;
}

interface Representative {
    name?: string;
    identificationCode?: string;
}

/** A signer already declared on the application, as returned by the API. */
export interface ApplicationSigner {
    type: SignerType;
    name: string;
    idCode: string;
    email?: string;
    phone?: string;
    address?: string;
    validityDate?: string;
    /** The stored file itself is not returned, only its name, so it can be shown when editing. */
    powerOfAttorneyFileName?: string;
}

interface ApplicationCustomer {
  id: number;
  name: string;
  registryNumber?: string;
  salesmanName?: string;
}

export enum SignerType {
  REPRESENTATIVE = 'REPRESENTATIVE',
  AUTHORIZED_REPRESENTATIVE = 'AUTHORIZED_REPRESENTATIVE'
}

export interface SignerRepresentative {
  name: string;
  idCode: string;
  /**
   * Only collected when the signer is somebody other than the application's contact person, whose
   * details the application already carries.
   */
  email?: string;
  phone?: string;
}

export interface RepresentativeSigner {
  type: SignerType.REPRESENTATIVE;
  representatives: SignerRepresentative[];
}

export interface AuthorizedRepresentativeSigner {
  type: SignerType.AUTHORIZED_REPRESENTATIVE;
  name: string;
  idCode: string;
  email: string;
  phone: string;
  address: string;
  validityDate: string;
  /** Sent as a separate multipart part, never inside the JSON request. */
  powerOfAttorney?: File;
}

export type CustomerApplicationSigner = RepresentativeSigner | AuthorizedRepresentativeSigner;

export interface BaseCustomerApplicationRequest {
  customerId?: number;
  contactPersonId?: number;
  signer?: CustomerApplicationSigner;
  pickupType?: string;
  pickupLocation?: string;
  pickupLocationId?: string | number;
  recipientEmail?: string;
  recipientPhone?: string;
  invoiceEmail?: string;
  eInvoice?: boolean;
  bankAccountNumber?: string;
  purchaseVolume?: string;
  creditLimit?: string;
  paymentDay?: string;
  invoicesPerMonth?: number;
  customerApplicationCards?: CustomerApplicationCardRequest[];
  contactEmail?: string;
  contactPhone?: string;
}

export interface CreateCustomerApplicationRequest extends BaseCustomerApplicationRequest {
  customerApplicationType: CustomerApplicationType;
}

export interface UpdateCustomerApplicationRequest extends BaseCustomerApplicationRequest {
  /** The server needs the type to know whether a signer is required. */
  customerApplicationType: CustomerApplicationType;
}

export interface CustomerApplicationFilters {
  type?: CustomerApplicationType;
}

export const CUSTOMER_APPLICATION_TYPE_FILTER_OPTIONS = [
  {
    label: 'Krediidi taotlus',
    value: CustomerApplicationType.CREDIT_APPLICATION,
  },
  {
    label: 'Krediidi suurendamine',
    value: CustomerApplicationType.INCREASE_CREDIT_APPLICATION,
  },
  {
    label: 'Krediitkaardi taotlus',
    value: CustomerApplicationType.PAYMENT_CARD_APPLICATION,
  },
  {
    label: 'Sooduskaardi taotlus',
    value: CustomerApplicationType.DISCOUNT_CARD_APPLICATION,
  },
];


export const CUSTOMER_APPLICATION_TYPE_MAP: {[key in CustomerApplicationType]: string} = {
    PAYMENT_CARD_APPLICATION: 'Krediitkaardi taotlus',
    DISCOUNT_CARD_APPLICATION: 'Sooduskaardi taotlus',
    CREDIT_APPLICATION: 'Krediidi taotlus',
    INCREASE_CREDIT_APPLICATION: 'Krediidi suurendamine',
    BILLING_TERMS_CHANGE_APPLICATION: 'Arveldustingimuste muutmine'
};

export const CUSTOMER_APPLICATION_STATUS_MAP: {[key in CustomerApplicationStatus]: string} = {
    PENDING: 'Ootel',
    IN_PROGRESS: 'Töös',
    WAITING_APPROVAL: 'Kinnitamise ootel',
    APPROVED: 'Kinnitatud',
    REJECTED: 'Tagasilükatud',
    CANCELED: 'Tühistatud'
};